import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CELULA, CascaDaLista, CabecalhoDaLista, LinhaDaLista, CelulaAcoes, CartaoDoTopo } from './shared/lista/PecasDaLista';
import { materialsService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import MaterialsPortal from './MaterialsPortal';
import { Icon } from './shared/Icon';
import CascaDeModal from './shared/CascaDeModal';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE } from '../utils/statusColors';
import { SortHeader } from './shared/SortableTableHeader';
import { DocumentViewerModal, DocViewerInfoItem } from './shared/DocumentViewerModal';
import { useConfirmarPerigo, useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, mensagemDoErroDeCarga } from '../design';
import { estadoDoLink, rotuloDoLink, confirmacaoDeLinkNovo } from '../utils/administrativo';

const CATEGORIES = [
  { value: 'Apresentacao', label: 'Apresentação' },
  { value: 'Roteiro', label: 'Roteiro' },
  { value: 'Argumentario', label: 'Argumentário' },
  { value: 'Case de Sucesso', label: 'Case de Sucesso' },
  { value: 'Whitepaper', label: 'Whitepaper' },
  { value: 'Video', label: 'Vídeo' },
  { value: 'Imagem', label: 'Imagem' },
  { value: 'Outro', label: 'Outro' },
];

const categoryLabel = (value: string) => CATEGORIES.find(c => c.value === value)?.label || value;

const STATUSES = [
  // Tons do domínio (10/10/2026); Arquivado é fora de uso, não alerta (era laranja). 23/10/2026 (padrão da Central): Ativo é
  // o normal, neutro.
  { value: 'Ativo', label: 'Ativo', color: TONE_BADGE.neutral },
  { value: 'Rascunho', label: 'Rascunho', color: 'bg-slate-600 text-slate-200' },
  { value: 'Arquivado', label: 'Arquivado', color: TONE_BADGE.neutral },
];

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};


const getFileIcon = (mimeType: string) => {
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (mimeType?.startsWith('video/')) return '🎬';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType === 'text/html') return '🌐';
  return '📁';
};


export default function MaterialsTab() {
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  // D6 (10/10/2026): os 8 alert() viram o aviso do HUB.
  const avisar = useAviso();
  const [viewMode, setViewMode] = useState<'portal' | 'manage'>('portal');
  const [materials, setMaterials] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [sortCol, setSortCol] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'Outro', status: 'Rascunho', tags: '', isPublic: true });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<any>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', category: '', status: '', tags: '', isPublic: true });
  const [saving, setSaving] = useState(false);

  // Version upload modal
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [versionMaterial, setVersionMaterial] = useState<any>(null);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionDragOver, setVersionDragOver] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const versionFileInputRef = useRef<HTMLInputElement>(null);

  // Version history modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyMaterial, setHistoryMaterial] = useState<any>(null);
  const [historyVersions, setHistoryVersions] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [downloadingVersion, setDownloadingVersion] = useState<number | null>(null);

  // Preview modal
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewMaterial, setPreviewMaterial] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  // Link copied feedback
  const [copiedId, setCopiedId] = useState<number | null>(null);

  // D5 (10/10/2026): a carga calava; com a API fora a lista dizia "Nenhum material encontrado", com o upload ao lado.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const loadData = useCallback(async () => {
    await rodarCarga(async () => {
      const [materialsData, statsData] = await Promise.all([
        materialsService.getAll(),
        materialsService.getStats(),
      ]);
      setMaterials(Array.isArray(materialsData) ? materialsData : []);
      setStats(statsData);
    });
  }, [rodarCarga]);
  const temFonte = carga.atualizadoEm !== null;
  const kpi = (n: number) => (temFonte ? n : textos.numeroSemFonte);

  useEffect(() => { loadData(); }, [loadData]);

  // Show portal view by default
  if (viewMode === 'portal') {
    return <MaterialsPortal onManageMaterials={() => setViewMode('manage')} />;
  }

  // Filter and sort materials
  const filteredMaterials = materials
    .filter(m => {
      if (categoryFilter !== 'Todos' && m.category !== categoryFilter) return false;
      if (statusFilter !== 'Todos' && m.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          m.title?.toLowerCase().includes(s) ||
          m.code?.toLowerCase().includes(s) ||
          m.description?.toLowerCase().includes(s) ||
          m.fileName?.toLowerCase().includes(s) ||
          (m.tags || []).some((t: string) => t.toLowerCase().includes(s))
        );
      }
      return true;
    })
    .sort((a, b) => {
      let valA = a[sortCol];
      let valB = b[sortCol];
      if (sortCol === 'fileSize' || sortCol === 'downloadCount' || sortCol === 'version') {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      if (sortCol === 'createdAt' || sortCol === 'updatedAt') {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      const strA = String(valA || '').toLowerCase();
      const strB = String(valB || '').toLowerCase();
      if (strA < strB) return sortDir === 'asc' ? -1 : 1;
      if (strA > strB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // Upload handlers
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadFile(file);
      if (!uploadForm.title) {
        setUploadForm(f => ({ ...f, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadForm.title) {
        setUploadForm(f => ({ ...f, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.title) return;
    setUploading(true);
    try {
      const tags = uploadForm.tags ? uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await materialsService.upload(uploadFile, {
        title: uploadForm.title,
        description: uploadForm.description || undefined,
        category: uploadForm.category,
        status: uploadForm.status,
        tags,
      });
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadForm({ title: '', description: '', category: 'Outro', status: 'Rascunho', tags: '', isPublic: true });
      loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao fazer upload', 'error');
    }
    setUploading(false);
  };

  // Edit handlers
  const openEditModal = (material: any) => {
    setEditingMaterial(material);
    setEditForm({
      title: material.title || '',
      description: material.description || '',
      category: material.category || 'Outro',
      status: material.status || 'Rascunho',
      tags: (material.tags || []).join(', '),
      isPublic: material.isPublic !== false,
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMaterial) return;
    setSaving(true);
    try {
      const tags = editForm.tags ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await materialsService.update(editingMaterial.id, {
        title: editForm.title,
        description: editForm.description || undefined,
        category: editForm.category,
        status: editForm.status,
        tags,
        isPublic: editForm.isPublic,
      });
      setEditModalOpen(false);
      setEditingMaterial(null);
      loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao salvar', 'error');
    }
    setSaving(false);
  };

  // Version upload handlers
  const openVersionModal = (material: any) => {
    setVersionMaterial(material);
    setVersionFile(null);
    setVersionModalOpen(true);
  };

  // Version history handlers
  const openHistoryModal = async (material: any) => {
    setHistoryMaterial(material);
    setHistoryVersions([]);
    setHistoryModalOpen(true);
    setLoadingHistory(true);
    try {
      const versions = await materialsService.getVersions(material.id);
      setHistoryVersions(versions);
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao carregar histórico de versões', 'error');
    }
    setLoadingHistory(false);
  };

  const handleDownloadVersion = async (version: number) => {
    if (!historyMaterial) return;
    setDownloadingVersion(version);
    try {
      await materialsService.downloadVersion(historyMaterial.id, version);
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao baixar versão', 'error');
    }
    setDownloadingVersion(null);
  };

  // Um handler só para o input e para o drop (regra de upload do CLAUDE.md).
  const escolherArquivoDeVersao = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (file) setVersionFile(file);
  };
  const handleVersionFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => escolherArquivoDeVersao(e.target.files);

  const handleUploadVersion = async () => {
    if (!versionMaterial || !versionFile) return;
    setUploadingVersion(true);
    try {
      await materialsService.uploadVersion(versionMaterial.id, versionFile);
      setVersionModalOpen(false);
      setVersionMaterial(null);
      setVersionFile(null);
      loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao fazer upload da nova versão', 'error');
    }
    setUploadingVersion(false);
  };

  // Preview handler
  const openPreview = async (material: any) => {
    setPreviewMaterial(material);
    try {
      const { url } = await materialsService.getDownloadUrl(material.id);
      setPreviewUrl(url);
      setPreviewModalOpen(true);
    } catch (e) {
      // Calava: o olho não abria nada.
      avisar(`Não consegui abrir o material: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  /** Painel lateral do modal do olho. */
  const infoDoMaterial = (material: any): DocViewerInfoItem[] => [
    { label: 'Status', value: material.status, tone: material.status === 'Publicado' ? 'success' : material.status === 'Arquivado' ? 'muted' : 'default' },
    { label: 'Categoria', value: categoryLabel(material.category) },
    { label: 'Versão', value: 'v' + material.version },
    { label: 'Arquivo', value: material.fileName },
    { label: 'Tamanho', value: formatFileSize(Number(material.fileSize)) },
    { label: 'Downloads', value: String(material.downloadCount ?? 0) },
    { label: 'Criado em', value: fmtDate(material.createdAt) },
  ];

  // Download handler
  const handleDownload = async (material: any) => {
    try {
      await materialsService.download(material.id);
      loadData(); // Refresh to update download count
    } catch (e) {
      avisar(`Não consegui baixar o material: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await materialsService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  // Gera (ou renova) o link público e copia. A URL vem do backend, não é montada aqui:
  // ela carrega um token opaco, e o `code` sequencial não resolve mais rota pública.
  // Reclicar num token ainda válido só estende a validade, então link já enviado ao
  // cliente continua funcionando. D4 (10/10/2026): no token EXPIRADO o servidor gera outro,
  // e o link que já está com o cliente morre; isso agora pergunta antes.
  const copyPublicLink = async (material: any) => {
    const link = estadoDoLink(material);
    if (link.tipo === 'expirado' && !await confirmar(confirmacaoDeLinkNovo(material, link.desde))) return;
    try {
      const { url } = await materialsService.createPublicLink(material.id, 30);
      await navigator.clipboard.writeText(url);
      setCopiedId(material.id);
      setTimeout(() => setCopiedId(null), 2000);
      loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao gerar link público', 'error');
    }
  };

  const revokePublicLink = async (material: any) => {
    if (!await confirmarPerigo(`Revogar o link público de "${material.title}"?\n\nO link que já foi enviado para de funcionar na hora.`)) return;
    try {
      await materialsService.revokePublicLink(material.id);
      loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao revogar link', 'error');
    }
  };

  // O cabeçalho ordenável é a peça compartilhada (era uma cópia local, com a seta laranja escrita à mão).
  const ordem = { col: sortCol, dir: sortDir };
  const Ordenavel = ({ col, label }: { col: string; label: string }) => (
    <SortHeader col={col} label={label} sort={ordem} onToggle={toggleSort} className="px-4 py-3 text-xs font-semibold uppercase" />
  );

  if (carga.estado === 'carregando' && !temFonte) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Carregando materiais...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setViewMode('portal')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar ao Portal
          </button>
          <h2 className="text-xl font-bold text-white">Gerenciar Materiais</h2>
        </div>
      </div>

      {/* Os cartões do topo (padrão das listas, 23/10/2026): o CartaoDoTopo, valor branco (o "Ativos" era verde). */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <CartaoDoTopo rotulo="Total de Materiais" valor={kpi(stats?.total || 0)} />
        <CartaoDoTopo rotulo="Materiais Ativos" valor={kpi(stats?.byStatus?.find((s: any) => s.status === 'Ativo')?.count || 0)} />
        <CartaoDoTopo rotulo="Downloads Totais" valor={kpi(stats?.totalDownloads || 0)} />
        <CartaoDoTopo rotulo="Rascunhos" valor={kpi(stats?.byStatus?.find((s: any) => s.status === 'Rascunho')?.count || 0)} />
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {carga.estado === 'pronto' && <button
          onClick={() => setUploadModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium"
        >
          <Icon name="upload" className="w-5 h-5" />
          Novo Material
        </button>}

        <div className="flex-1" />

        <div className="relative">
          <Icon name="search" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`pl-10 pr-4 py-2 rounded-lg placeholder-slate-500 focus:outline-none w-64 ${campo}`}
          />
        </div>

        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className={`px-3 py-2 ${campo} rounded-lg focus:outline-none`}
        >
          <option value="Todos">Todas Categorias</option>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className={`px-3 py-2 ${campo} rounded-lg focus:outline-none`}
        >
          <option value="Todos">Todos Status</option>
          {STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <button
          onClick={loadData}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
          title="Atualizar"
        >
          <Icon name="refresh" className="w-5 h-5" />
        </button>
      </div>

      {/* Materials Table */}
      <EstadoDaLista carga={carga} temDados={temFonte} oQue="os materiais" onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      <CascaDaLista>
        <div className="overflow-x-auto">
          <table className="w-full">
            <CabecalhoDaLista>
                <Ordenavel col="code" label="Código" />
                <Ordenavel col="title" label="Título" />
                <Ordenavel col="category" label="Categoria" />
                <Ordenavel col="status" label="Status" />
                <Ordenavel col="fileName" label="Arquivo" />
                <Ordenavel col="fileSize" label="Tamanho" />
                <Ordenavel col="version" label="Versão" />
                <Ordenavel col="downloadCount" label="Downloads" />
                <Ordenavel col="createdAt" label="Criado" />
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase">Ações</th>
            </CabecalhoDaLista>
            <tbody className="divide-y divide-central-borda">
              {filteredMaterials.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-500">
                    Nenhum material encontrado
                  </td>
                </tr>
              ) : (
                filteredMaterials.map(material => {
                  const statusInfo = STATUSES.find(s => s.value === material.status);
                  return (
                    <LinhaDaLista key={material.id} apagada={material.status === 'Arquivado'}>
                      <td className={`${CELULA} text-central-texto font-mono text-sm`}>{material.code}</td>
                      <td className={CELULA}>
                        <div className="text-central-texto font-medium">{material.title}</div>
                        {rotuloDoLink(estadoDoLink(material)).selo && (
                          <div className={`text-xs ${estadoDoLink(material).tipo === 'expirado' ? 'text-amber-400 font-medium' : 'text-slate-500'}`} data-testid="selo-do-link">
                            {rotuloDoLink(estadoDoLink(material)).selo}
                          </div>
                        )}
                        {material.description && (
                          <div className="text-slate-500 text-sm truncate max-w-xs">{material.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{categoryLabel(material.category)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo?.color || ''}`}>
                          {material.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getFileIcon(material.mimeType)}</span>
                          <span className="text-slate-400 text-sm truncate max-w-[150px]">{material.fileName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{formatFileSize(Number(material.fileSize))}</td>
                      <td className="px-4 py-3 text-slate-400 text-center">v{material.version}</td>
                      <td className="px-4 py-3 text-slate-300 text-center">{material.downloadCount}</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{fmtDate(material.createdAt)}</td>
                      {/* O dia a dia à vista (ver, baixar, copiar o link, nova versão, histórico) e o editar; revogar o link e
                          excluir no ⋯, em perigo (23/10/2026, padrão das listas). O "Copiado!" verde é o retorno do clique e fica. */}
                      <CelulaAcoes
                        rotuloDoMenu={`Mais ações do material ${material.code}`}
                        onEditar={() => openEditModal(material)}
                        Icon={Icon}
                        antes={<>
                          {(material.mimeType?.startsWith('image/') || material.mimeType === 'application/pdf' || material.mimeType === 'text/html') && (
                            <button
                              onClick={() => openPreview(material)}
                              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                              title="Visualizar"
                            >
                              <Icon name="eye" className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDownload(material)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                            title="Download"
                          >
                            <Icon name="download" className="w-4 h-4" />
                          </button>
                          {/* Só oferece o link quando o material é publico de fato. O
                              `code` e sequencial e a rota /m/CODE nao pede login, entao
                              material interno (roteiro, argumentario) fica fora. */}
                          {material.status === 'Ativo' && material.isPublic !== false && (
                            <button
                              onClick={() => copyPublicLink(material)}
                              className={`p-1.5 hover:bg-slate-600 rounded ${copiedId === material.id ? 'text-green-400' : 'text-slate-400 hover:text-white'}`}
                              title={copiedId === material.id ? 'Link copiado!' : rotuloDoLink(estadoDoLink(material)).botao}
                            >
                              {copiedId === material.id ? (
                                <span className="text-xs font-medium">Copiado!</span>
                              ) : (
                                <Icon name="link" className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => openVersionModal(material)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                            title="Nova versão"
                          >
                            <Icon name="upload" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openHistoryModal(material)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                            title="Histórico de versões"
                          >
                            <Icon name="clock" className="w-4 h-4" />
                          </button>

                        </>}
                        itens={[
                          ...(material.publicToken ? [{ label: 'Revogar link público', onClick: () => revokePublicLink(material), danger: true }] : []),
                          { label: 'Excluir', onClick: () => setDeleteConfirm(material), danger: true, separadoAntes: !!material.publicToken },
                        ]}
                      />
                    </LinhaDaLista>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CascaDaLista>
      </EstadoDaLista>

      {/* Upload Modal */}
      {uploadModalOpen && (
        // Formulário: não fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="sm"
          titulo="Novo Material"
          onClose={() => { setUploadModalOpen(false); setUploadFile(null); }}
          corpoClassName="p-4 space-y-4"
          rodape={
            <>
              <button
                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || !uploadForm.title || uploading}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Enviando...' : 'Enviar'}
              </button>
            </>
          }
        >
              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary bg-primary/10' : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.mp4,.webm,.html"
                  className="hidden"
                />
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-3xl">{getFileIcon(uploadFile.type)}</span>
                    <div className="text-left">
                      <p className="text-white font-medium">{uploadFile.name}</p>
                      <p className="text-slate-400 text-sm">{formatFileSize(uploadFile.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Icon name="upload" className="w-12 h-12 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-400">Arraste um arquivo ou clique para selecionar</p>
                    <p className="text-slate-500 text-sm mt-1">PDF, Imagens ou Vídeos (max 100MB)</p>
                  </>
                )}
              </div>

              <div>
                <label className="text-slate-400 text-sm">Título *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  placeholder="Ex: Apresentação Comercial 2026"
                />
              </div>

              <div>
                <label className="text-slate-400 text-sm">Descrição</label>
                <textarea
                  value={uploadForm.description}
                  onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none resize-none`}
                  rows={2}
                  placeholder="Descrição do material..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm">Categoria</label>
                  <select
                    value={uploadForm.category}
                    onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                    className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-sm">Status</label>
                  <select
                    value={uploadForm.status}
                    onChange={e => setUploadForm(f => ({ ...f, status: e.target.value }))}
                    className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  >
                    {STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-slate-400 text-sm">Tags (separadas por vírgula)</label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={e => setUploadForm(f => ({ ...f, tags: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  placeholder="Ex: vendas, institucional, 2026"
                />
              </div>
        </CascaDeModal>
      )}

      {/* Edit Modal */}
      {editModalOpen && editingMaterial && (
        // Formulário: não fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="sm"
          titulo="Editar Material"
          subtitulo={`${editingMaterial.code} · v${editingMaterial.version}`}
          onClose={() => setEditModalOpen(false)}
          corpoClassName="p-4 space-y-4"
          rodape={
            <>
              <button
                onClick={() => setEditModalOpen(false)}
                className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editForm.title || saving}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </>
          }
        >
              <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                <span className="text-2xl">{getFileIcon(editingMaterial.mimeType)}</span>
                <div>
                  <p className="text-white font-medium">{editingMaterial.fileName}</p>
                  <p className="text-slate-400 text-sm">{editingMaterial.code} - v{editingMaterial.version}</p>
                </div>
              </div>

              <div>
                <label className="text-slate-400 text-sm">Título *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              <div>
                <label className="text-slate-400 text-sm">Descrição</label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none resize-none`}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm">Categoria</label>
                  <select
                    value={editForm.category}
                    onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                    className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-sm">Status</label>
                  <select
                    value={editForm.status}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                    className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  >
                    {STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-slate-400 text-sm">Tags (separadas por vírgula)</label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              {/* O link /m/CODE nao pede login e o codigo e sequencial, entao qualquer um
                  enumera. Material interno da equipe (roteiro, argumentario) deve ficar
                  desmarcado: continua na tela, some do link. */}
              <div className="flex items-start gap-3 p-3 bg-central-fundo/50 border border-central-borda rounded-lg">
                <input
                  id="edit-is-public"
                  type="checkbox"
                  checked={editForm.isPublic}
                  onChange={e => setEditForm(f => ({ ...f, isPublic: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-primary"
                />
                <label htmlFor="edit-is-public" className="text-sm cursor-pointer">
                  <span className="text-slate-200 font-medium">Permitir link público</span>
                  <span className="block text-slate-400 text-xs mt-0.5">
                    Deixe desmarcado para material interno. O link público abre sem login, e o
                    código é sequencial, então quem tiver um link consegue tentar os outros.
                  </span>
                </label>
              </div>
        </CascaDeModal>
      )}

      {/* Version Upload Modal */}
      {versionModalOpen && versionMaterial && (
        // Arquivo escolhido: não fecha ao clicar fora, como já era. A primária roxa
        // vira o laranja. Só moldura: o handleUploadVersion (v → v+1) não muda.
        <CascaDeModal
          largura="xs"
          titulo="Nova Versão"
          subtitulo={versionMaterial.code}
          onClose={() => setVersionModalOpen(false)}
          // A caixa inteira aceita o arquivo (antes só o clique na zona funcionava).
          aoSoltarArquivos={(e) => {
            e.preventDefault();
            setVersionDragOver(false);
            if (!uploadingVersion) escolherArquivoDeVersao(e.dataTransfer.files);
          }}
          corpoClassName="p-4 space-y-4"
          rodape={
            <>
              <button
                onClick={() => setVersionModalOpen(false)}
                className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleUploadVersion}
                disabled={!versionFile || uploadingVersion}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingVersion ? 'Enviando...' : 'Enviar Nova Versão'}
              </button>
            </>
          }
        >
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">{versionMaterial.title}</p>
                <p className="text-slate-400 text-sm">{versionMaterial.code} - Versão atual: v{versionMaterial.version}</p>
              </div>

              <div
                onClick={() => versionFileInputRef.current?.click()}
                onDragOver={() => setVersionDragOver(true)}
                onDragLeave={() => setVersionDragOver(false)}
                className={`border-2 border-dashed border-slate-600 rounded-xl p-6 text-center cursor-pointer hover:border-slate-500 ${versionDragOver ? 'ring-2 ring-primary' : ''}`}
              >
                <input
                  ref={versionFileInputRef}
                  type="file"
                  onChange={handleVersionFileSelect}
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.mp4,.webm,.html"
                  className="hidden"
                />
                {versionFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">{getFileIcon(versionFile.type)}</span>
                    <div className="text-left">
                      <p className="text-white font-medium">{versionFile.name}</p>
                      <p className="text-slate-400 text-sm">{formatFileSize(versionFile.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Icon name="upload" className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-400">Arraste o novo arquivo ou clique para selecionar</p>
                  </>
                )}
              </div>

              <p className="text-slate-500 text-sm text-center">
                Será criada a versão v{versionMaterial.version + 1}
              </p>
        </CascaDeModal>
      )}

      {/* Version History Modal */}
      {historyModalOpen && historyMaterial && (
        // Só leitura: antes não fechava ao clicar fora; agora fecha. O título é o
        // material; código e versão atual vão para a linha mono.
        <CascaDeModal
          largura="base"
          fecharAoClicarFora
          titulo={`Histórico de Versões · ${historyMaterial.title}`}
          subtitulo={`${historyMaterial.code} — Versão atual: v${historyMaterial.version}`}
          onClose={() => setHistoryModalOpen(false)}
          corpoClassName="p-4"
          rodape={
            <button
              onClick={() => setHistoryModalOpen(false)}
              className="w-full px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
            >
              Fechar
            </button>
          }
        >
              {loadingHistory ? (
                <div className="text-center py-8 text-slate-400">Carregando...</div>
              ) : historyVersions.length === 0 ? (
                <div className="text-center py-8 text-slate-400">Nenhuma versão registrada.</div>
              ) : (
                <ul className="space-y-2">
                  {historyVersions.map((v: any) => {
                    const isCurrent = v.version === historyMaterial.version;
                    return (
                      <li
                        key={v.id}
                        className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                          isCurrent ? `${TONE_SURFACE.progress} ${TONE_BORDER.progress}` : 'bg-slate-700/40 border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="text-xl">{getFileIcon(v.mimeType)}</div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-bold">v{v.version}</span>
                              {isCurrent && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${TONE_BADGE.progress}`}>
                                  atual
                                </span>
                              )}
                              <span className="text-slate-300 text-sm truncate">{v.fileName}</span>
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {formatFileSize(Number(v.fileSize))} ·{' '}
                              {new Date(v.createdAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                              {v.uploadedByName && ` · ${v.uploadedByName}`}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadVersion(v.version)}
                          disabled={downloadingVersion === v.version}
                          className="flex-shrink-0 p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded disabled:opacity-50"
                          title="Baixar esta versão"
                        >
                          <Icon name="download" className="w-4 h-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
        </CascaDeModal>
      )}

      {/* Preview Modal (componente compartilhado — ver shared/DocumentViewerModal.tsx) */}
      {previewModalOpen && previewMaterial && (
        <DocumentViewerModal
          title={previewMaterial.title}
          subtitle={`${previewMaterial.code} · v${previewMaterial.version}`}
          fileUrl={previewUrl}
          mimeType={previewMaterial.mimeType}
          fileName={previewMaterial.fileName}
          loading={!previewUrl}
          accent='primary'
          onClose={() => setPreviewModalOpen(false)}
          info={infoDoMaterial(previewMaterial)}
          headerActions={
            <button
              onClick={() => handleDownload(previewMaterial)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200"
              title="Download"
            >
              <Icon name="download" className="w-4 h-4" />
              Baixar
            </button>
          }
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        // Confirmação de perigo: não fechava ao clicar fora e continua não fechando;
        // ganha o ×. O crachá vermelho de lixeira sai (§10.4); "Excluir" segue vermelho.
        <CascaDeModal
          largura="xs"
          titulo="Excluir Material"
          onClose={() => setDeleteConfirm(null)}
          corpoClassName="px-5 py-4"
          rodape={
            <>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 bg-danger text-white rounded-lg hover:bg-danger-strong font-medium"
              >
                Excluir
              </button>
            </>
          }
        >
          <p className="text-slate-400">
            Deseja realmente excluir "{deleteConfirm.title}"? Esta ação não pode ser desfeita.
          </p>
        </CascaDeModal>
      )}
    </div>
  );
}
